import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

/**
 * A button's label that turns into a spinner while the button is busy.
 *
 * ```html
 * <button class="btn btn-primary" [disabled]="saving">
 *   <plys-button-label [loading]="saving">{{ 'global.save' | transloco }}</plys-button-label>
 * </button>
 * ```
 *
 * Replaces the `*ngIf="!loading; else spinner"` + `<ng-template #spinner>` pair that was written out
 * at fifteen call sites, several of which sized the spinner differently and so made the button change
 * size the moment it was pressed.
 *
 * The label is hidden rather than removed, so it goes on holding the button open: neither its height
 * nor its width can change. `styles/spinner.scss` separately sizes any spinner inside a `.btn` to the
 * label's line box and strokes it in the button's own colour, which is why no variant or diameter is
 * accepted here — inside a button those inputs never had any effect.
 */
@Component({
  selector: 'plys-button-label',
  standalone: true,
  imports: [MatProgressSpinnerModule],
  template: `
    <span class="button-label" [class.is-loading]="loading">
      <span class="button-label-text"><ng-content></ng-content></span>
      @if (loading) {
        <mat-spinner class="button-label-spinner" [diameter]="20"></mat-spinner>
      }
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./button-label.component.scss'],
})
export class ButtonLabelComponent {
  @Input() loading = false;
}
